// src/new/components/monitor/TriageChat/TriageChatModal.tsx
import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { TriageContext } from './triageContext';
import { TriageChatContainer } from './TriageChatContainer';

export interface TriageChatModalProps {
  open: boolean;
  context: TriageContext | null;
  onClose: () => void;
  /** False for a click-opened session (any `open()` source other than the
   *  notification deep link) — see TriageChatProvider, which computes this
   *  from the source and always passes an explicit value. */
  autoSeed: boolean;
}

export const TriageChatModal: React.FC<TriageChatModalProps> = ({
  open,
  context,
  onClose,
  autoSeed,
}) => {
  return (
    <Dialog
      open={open && !!context}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
      data-testid="triage-chat-modal"
    >
      <DialogTitle
        component="div"
        sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Circular avatar chip with pulse icon */}
          <Box
            aria-hidden="true"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <TimelineOutlinedIcon sx={{ color: '#10b981', fontSize: 22 }} />
          </Box>

          {/* Title + badge + tagline */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" component="span" fontWeight={700}>
                Monitoring Agent
              </Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  px: 0.75,
                  py: 0.15,
                  borderRadius: '999px',
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#10b981',
                  lineHeight: 'var(--line-height-sm)',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#10b981',
                    display: 'inline-block',
                  }}
                />
                Running
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
            >
              Chat-based observability for your data stack
            </Typography>
          </Box>

          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{ ml: 'auto', mt: -0.5 }}
            data-testid="triage-modal-close"
            aria-label="Close triage chat"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{ display: 'flex', minHeight: 0, flex: 1, overflow: 'hidden' }}
      >
        {context && (
          <TriageChatContainer
            key={`${context.resourceType}-${context.resourceId}-${context.runId ?? ''}`}
            context={context}
            autoSeed={autoSeed}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
